/**
 * Autonomy Policy Matrix — pure threshold engine for risk-tiered auto-approve.
 *
 * At scale, operators face dozens of agent proposals a day. This module lets
 * an admin declare, per agent × action-type, whether proposals should:
 *
 *   • 'auto'  — auto-approve at/below a $ impact ceiling (`maxImpact`; null
 *               means no ceiling → always auto),
 *   • 'ask'   — always route to the human approvals inbox,
 *   • 'block' — never allow, regardless of impact.
 *
 * Rules support the '*' wildcard on either dimension. Resolution is
 * most-specific-wins: exact agent + exact type beats a wildcard on one
 * dimension, which beats the double wildcard, which beats the policy's
 * `defaultMode` ('ask' — safe by construction). At equal specificity a
 * 'block' rule wins ties, so a block can never be shadowed by a same-rank
 * 'auto'.
 *
 * Pure and IO-free: the policy is passed in, never read from a store —
 * callers own persistence. This keeps every decision unit-testable and lets
 * triage surfaces (approvals inbox, schedulers) share one deterministic
 * gate.
 */

/** Wildcard token matching any agent id or action type. */
export const WILDCARD = '*';

export type PolicyMode = 'auto' | 'ask' | 'block';

export type PolicyRule = {
  /** Agent id, or '*' for any agent. */
  agentId: string;
  /** Action type (e.g. 'create_task'), or '*' for any type. */
  actionType: string;
  mode: PolicyMode;
  /**
   * For mode 'auto': auto-approve at/below this $ impact; above it the
   * proposal escalates to 'ask'. null = no ceiling (always auto).
   * Ignored for 'ask' / 'block'.
   */
  maxImpact: number | null;
};

export type AutonomyPolicy = {
  rules: PolicyRule[];
  /** Fallback when no rule matches. Always 'ask' — safe by default. */
  defaultMode: 'ask';
};

/** A fresh, empty policy: everything asks a human. */
export function emptyPolicy(): AutonomyPolicy {
  return { rules: [], defaultMode: 'ask' };
}

/** True when `rule` applies to the given agent + action type. */
function ruleMatches(rule: PolicyRule, agentId: string, actionType: string): boolean {
  return (
    (rule.agentId === agentId || rule.agentId === WILDCARD) &&
    (rule.actionType === actionType || rule.actionType === WILDCARD)
  );
}

/**
 * Specificity rank of a matching rule. Exact agent outranks exact type
 * (an agent-scoped rule is a deliberate per-agent decision), and any
 * exact dimension outranks the double wildcard.
 */
function specificity(rule: PolicyRule): number {
  return (rule.agentId !== WILDCARD ? 2 : 0) + (rule.actionType !== WILDCARD ? 1 : 0);
}

const MODE_PRIORITY: Record<PolicyMode, number> = { block: 2, ask: 1, auto: 0 };

/**
 * Find the governing rule for an agent + action type. Most-specific match
 * wins; ties broken toward the more restrictive mode (block > ask > auto).
 * When nothing matches, a synthetic rule carrying the policy's defaultMode
 * is returned so callers always get a concrete PolicyRule.
 */
export function resolvePolicy(
  policy: AutonomyPolicy,
  agentId: string,
  actionType: string,
): PolicyRule {
  let best: PolicyRule | null = null;
  for (const rule of policy.rules) {
    if (!ruleMatches(rule, agentId, actionType)) continue;
    if (
      best == null ||
      specificity(rule) > specificity(best) ||
      (specificity(rule) === specificity(best) &&
        MODE_PRIORITY[rule.mode] > MODE_PRIORITY[best.mode])
    ) {
      best = rule;
    }
  }
  return (
    best ?? {
      agentId,
      actionType,
      mode: policy.defaultMode,
      maxImpact: null,
    }
  );
}

/**
 * Decide what happens to a proposed action:
 *   'block' → 'block' always (impact irrelevant);
 *   'auto'  → 'auto' when maxImpact is null or impact ≤ maxImpact, else
 *             escalates to 'ask';
 *   'ask' / unknown → 'ask'.
 */
export function evaluateAction(
  policy: AutonomyPolicy,
  action: { agentId: string; actionType: string; impact: number },
): PolicyMode {
  const rule = resolvePolicy(policy, action.agentId, action.actionType);
  if (rule.mode === 'block') return 'block';
  if (rule.mode === 'auto') {
    if (rule.maxImpact == null) return 'auto';
    return action.impact <= rule.maxImpact ? 'auto' : 'ask';
  }
  return 'ask';
}

/**
 * Return a new policy with the rule for (agentId, actionType) replaced or
 * appended. Immutable — the input policy is untouched. Used by the config
 * UI so each cell edit produces a fresh policy for the caller to persist.
 */
export function upsertRule(policy: AutonomyPolicy, rule: PolicyRule): AutonomyPolicy {
  const idx = policy.rules.findIndex(
    (r) => r.agentId === rule.agentId && r.actionType === rule.actionType,
  );
  const rules =
    idx >= 0
      ? policy.rules.map((r, i) => (i === idx ? rule : r))
      : [...policy.rules, rule];
  return { ...policy, rules };
}

/** Next mode in the auto → ask → block → auto cycle (UI cell click). */
export function cycleMode(mode: PolicyMode): PolicyMode {
  switch (mode) {
    case 'auto':
      return 'ask';
    case 'ask':
      return 'block';
    case 'block':
      return 'auto';
  }
}
